import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { getPrimaryUserIdForAccount } from './firestoreService';

const defaultOrgChartData = () => ({
  employees: [],
  totalRevenue: 1000000,
  isChartFinalized: false,
  finalizedEmployees: null,
  proposedEmployees: null,
  contractors1099: [],
  hiringOutline: { order: [], hiredAt: {} },
  version: 0
});

/**
 * Save org chart data. When accountId is provided, saves to account (company-wide).
 * Otherwise saves to current user doc (user-scoped).
 */
export const saveOrgChartData = async (orgChartData, accountId = null) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to save org chart data');
    }

    const versionIncrement = (existing) => (existing?.version ?? 0) + 1;

    if (accountId) {
      const accountRef = doc(db, 'accounts', accountId);
      const accountDoc = await getDoc(accountRef);
      const existing = accountDoc.exists() ? accountDoc.data().orgChartData : null;
      const orgChartDataWithMetadata = {
        employees: orgChartData.employees || [],
        totalRevenue: orgChartData.totalRevenue || 1000000,
        isChartFinalized: orgChartData.isChartFinalized || false,
        finalizedEmployees: orgChartData.finalizedEmployees || null,
        proposedEmployees: orgChartData.proposedEmployees || null,
        contractors1099: orgChartData.contractors1099 || [],
        hiringOutline: orgChartData.hiringOutline || { order: [], hiredAt: {} },
        lastUpdated: serverTimestamp(),
        version: versionIncrement(existing)
      };
      await updateDoc(accountRef, {
        orgChartData: orgChartDataWithMetadata,
        lastUpdated: serverTimestamp()
      });
      console.log('[OrgChart] Saved to company account', { accountId, employeeCount: orgChartDataWithMetadata.employees?.length });
      return orgChartDataWithMetadata;
    }

    const userDocRef = doc(db, 'users', auth.currentUser.uid);
    const userDoc = await getDoc(userDocRef);
    const currentUserData = userDoc.exists() ? userDoc.data() : {};
    const existing = currentUserData.businessAssessment?.orgChartData;
    const orgChartDataWithMetadata = {
      employees: orgChartData.employees || [],
      totalRevenue: orgChartData.totalRevenue || 1000000,
      isChartFinalized: orgChartData.isChartFinalized || false,
      finalizedEmployees: orgChartData.finalizedEmployees || null,
      proposedEmployees: orgChartData.proposedEmployees || null,
      contractors1099: orgChartData.contractors1099 || [],
      hiringOutline: orgChartData.hiringOutline || { order: [], hiredAt: {} },
      lastUpdated: serverTimestamp(),
      version: versionIncrement(existing)
    };
    await updateDoc(userDocRef, {
      'businessAssessment.orgChartData': orgChartDataWithMetadata,
      'operationsData.orgChartData': orgChartDataWithMetadata,
      'operationsData.employees': orgChartDataWithMetadata.employees,
      'operationsData.totalRevenue': orgChartDataWithMetadata.totalRevenue,
      'operationsData.isChartFinalized': orgChartDataWithMetadata.isChartFinalized,
      lastUpdated: serverTimestamp()
    });
    console.log('[OrgChart] Saved to user doc', { userId: auth.currentUser.uid });
    return orgChartDataWithMetadata;
  } catch (error) {
    console.error('Error saving org chart data:', error);
    throw error;
  }
};

/**
 * Load org chart from a specific user doc (used for company fallback when account has no data).
 */
async function loadOrgChartDataFromUser(userId) {
  const userDocRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userDocRef);
  if (userDoc.exists()) {
    const userData = userDoc.data();
    const data = userData.businessAssessment?.orgChartData || userData.operationsData?.orgChartData;
    return data ? { ...defaultOrgChartData(), ...data } : defaultOrgChartData();
  }
  return defaultOrgChartData();
}

/**
 * Load org chart data. When accountId is provided, loads from account (company-wide).
 * If account has no/empty org chart, falls back to primary user's doc so team members see company data.
 * Otherwise loads from current user doc (user-scoped).
 */
export const loadOrgChartData = async (accountId = null) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to load org chart data');
    }

    if (accountId) {
      const accountRef = doc(db, 'accounts', accountId);
      const accountDoc = await getDoc(accountRef);
      const accountExists = accountDoc.exists();
      const data = accountExists ? accountDoc.data().orgChartData : null;
      const employees = data?.employees ?? [];
      const hasData = Array.isArray(employees) && employees.length > 0;

      console.log('[OrgChart] loadOrgChartData company', {
        accountId,
        accountExists,
        fromAccount: !!data,
        employeeCount: employees.length
      });

      if (hasData && data) {
        return { ...defaultOrgChartData(), ...data };
      }

      // Fallback: account has no org chart yet (e.g. data only on primary user) — load from primary user
      const primaryUserId = await getPrimaryUserIdForAccount(accountId);
      if (primaryUserId) {
        const fallback = await loadOrgChartDataFromUser(primaryUserId);
        const fallbackCount = (fallback.employees || []).length;
        console.log('[OrgChart] loadOrgChartData fallback to primary user', {
          primaryUserId,
          employeeCount: fallbackCount
        });
        return fallback;
      }

      return defaultOrgChartData();
    }

    const userDocRef = doc(db, 'users', auth.currentUser.uid);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const out = userData.businessAssessment?.orgChartData || defaultOrgChartData();
      console.log('[OrgChart] loadOrgChartData user-scoped', { userId: auth.currentUser.uid, employeeCount: (out.employees || []).length });
      return out;
    }
    return defaultOrgChartData();
  } catch (error) {
    console.error('Error loading org chart data:', error);
    throw error;
  }
};

export const updateOrgChartEmployee = async (employeeId, employeeData, accountId = null) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update org chart employee');
    }
    const currentOrgChart = await loadOrgChartData(accountId);
    const updatedEmployees = (currentOrgChart.employees || []).map(emp =>
      emp.id === employeeId ? { ...emp, ...employeeData } : emp
    );
    await saveOrgChartData({ ...currentOrgChart, employees: updatedEmployees }, accountId);
    return updatedEmployees;
  } catch (error) {
    console.error('Error updating org chart employee:', error);
    throw error;
  }
};

export const deleteOrgChartEmployee = async (employeeId, accountId = null) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to delete org chart employee');
    }
    const currentOrgChart = await loadOrgChartData(accountId);
    const employees = currentOrgChart.employees || [];
    const employeeToDelete = employees.find(emp => emp.id === employeeId);
    const managerId = employeeToDelete ? employeeToDelete.reportsTo : null;
    const updatedEmployees = employees
      .filter(emp => emp.id !== employeeId)
      .map(emp => (emp.reportsTo === employeeId ? { ...emp, reportsTo: managerId } : emp));
    await saveOrgChartData({ ...currentOrgChart, employees: updatedEmployees }, accountId);
    return updatedEmployees;
  } catch (error) {
    console.error('Error deleting org chart employee:', error);
    throw error;
  }
};

export const addOrgChartEmployee = async (employeeData, accountId = null) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to add org chart employee');
    }
    const currentOrgChart = await loadOrgChartData(accountId);
    const newEmployee = {
      ...employeeData,
      id: employeeData.id || Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    const updatedEmployees = [...(currentOrgChart.employees || []), newEmployee];
    await saveOrgChartData({ ...currentOrgChart, employees: updatedEmployees }, accountId);
    return newEmployee;
  } catch (error) {
    console.error('Error adding org chart employee:', error);
    throw error;
  }
}; 